import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { BotCardType, DialogMessage, HistoryItemResponse } from '@shared/index';
import { cardsHtmlSchema, cardsIframeSchema, cardsImageSchema, cardsLoadingSchema, cardsVimeoSchema, cardsYoutubeSchema } from '@shared/ragai.schemas';
import { firstValueFrom } from 'rxjs';
import { EventManagerService } from 'src/app/shared/services/event-manager/event-manager.service';
import { z } from 'zod';
import { BUTTONCARDBUTTON } from '../components/ai-bot/utils/bot-chat-dialog/message-card/button-card/button-card.component';
import { HelperManagerService } from './helper-manager.service';
import { StageManagerService } from './stage-manager.service';

/**
 * Zeigt Bot-Cards (Buttons, Bilder, iFrames, HTML, Videos, Lade-Indikator) im Chat an (`RAGAI.CARDS`).
 * @remarks
 * Cards werden - anders als `RAGAI.OVERLAY` - direkt **in den Chatverlauf** eingefügt und persistiert (als
 * Verlaufsnachricht mit `role: 'botcard'`); Overlays sind modale, nicht-persistente UI-Elemente über dem Chat.
 * Jede Card löst `RAGAI.CARDS.ADD` sowie `RAGAI.CARDS.ADD.<type>` Events aus (außer {@link debugEvent}, das
 * bewusst keine Events feuert, um Debug-Ausgaben nicht in den regulären Event-Strom zu mischen).
 * Für Diagramme siehe stattdessen `RAGAI.CHARTS`.
 */
@Injectable({
  providedIn: 'root'
})
export class CardsManagerService {
  constructor(
    private _http: HttpClient,
    private stageManager: StageManagerService,
    private botIdService: BotidService,
    private helperManager: HelperManagerService, // Assuming this is the correct service for getting image URLs
    private eventManager: EventManagerService
  ) { }

  /**
   * Zeigt eine Button-Card mit anklickbaren Optionen an.
   * @param buttons Die anzuzeigenden Buttons.
   * @param cssClass Optionale CSS-Klasse für die Card.
   */
  public async buttons(buttons: BUTTONCARDBUTTON[], cssClass: string = '') {
    buttons = buttons.map((b) => {
      if (typeof b === 'string') {
        return b;
      }
      return { ...b, onClick: "" + b.onClick };
    });
    await this.addBotCard({ type: BotCardType.buttonsCard, data: { buttons, cssClass } });
  }

  /**
   * Zeigt eine iFrame-Card an.
   * @param url URL der einzubettenden Seite, oder ein Objekt gemäß `cardsIframeSchema`.
   * @param cssClass Optionale CSS-Klasse für die Card.
   * @returns Nichts.
   */
  public async iframe(url: string | z.input<typeof cardsIframeSchema>, cssClass: string = ''): Promise<void> {
    const parsed = cardsIframeSchema.safeParse(url);
    if (parsed.success && typeof url === 'object') {
      cssClass = parsed.data.cssClass ?? cssClass;
      url = parsed.data.url;
    }
    await this.addBotCard({ type: BotCardType.iframeCard, data: { url: url as string, cssClass } });
  }

  /**
   * Zeigt eine HTML-Card an.
   * @param html Der anzuzeigende HTML-Inhalt, oder ein Objekt gemäß `cardsHtmlSchema`.
   * @param cssClass Optionale CSS-Klasse für die Card.
   * @param id Optionale Element-ID für die Card.
   * @returns Nichts.
   */
  public async html(html: string | z.input<typeof cardsHtmlSchema>, cssClass: string = '', id = ''): Promise<void> {
    const parsed = cardsHtmlSchema.safeParse(html);
    if (parsed.success && typeof html === 'object') {
      cssClass = parsed.data.cssClass ?? cssClass;
      id = parsed.data.id ?? id;
      html = parsed.data.html;
    }
    await this.addBotCard({ type: BotCardType.htmlCard, data: { html: html as string, cssClass, id } });
  }

  /**
   * Wie {@link html}, löst aber keine `RAGAI.CARDS.ADD`-Events aus. Wird für interne Debug-Ausgaben verwendet.
   * @param html Der anzuzeigende HTML-Inhalt.
   * @param cssClass Optionale CSS-Klasse für die Card.
   * @param id Optionale Element-ID für die Card.
   */
  public async debugEvent(html: string, cssClass: string = '', id = '') {
    await this.addBotCard({ type: BotCardType.htmlCard, data: { html, cssClass, id } }, false);
  }

  /**
   * Zeigt einen Lade-Indikator im Chat an.
   * @param text Optionaler Ladetext, oder ein Objekt gemäß `cardsLoadingSchema`.
   * @param icon Optionales Icon für den Indikator.
   * @param cssClass Optionale CSS-Klasse für die Card.
   */
  public loading(text: string | z.input<typeof cardsLoadingSchema> = '', icon: string = '', cssClass: string = ''): void {
    const parsed = cardsLoadingSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      icon = parsed.data.icon ?? icon;
      cssClass = parsed.data.cssClass ?? cssClass;
      text = parsed.data.text ?? '';
    }
    const payload = { type: BotCardType.loadingCard, data: { text, cssClass, icon } };
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.CARDS.loading", payload);
  }

  /** Entfernt den aktuell angezeigten Lade-Indikator. */
  public removeLoading() {
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.CARDS.removeLoading");
  }

  /**
   * Zeigt eine YouTube-Video-Card an.
   * @param id Die YouTube-Video-ID, oder ein Objekt gemäß `cardsYoutubeSchema`.
   * @param cssClass Optionale CSS-Klasse für die Card.
   * @returns Nichts.
   */
  public async youtube(id: string | z.input<typeof cardsYoutubeSchema>, cssClass: string = ''): Promise<void> {
    const parsed = cardsYoutubeSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') {
      cssClass = parsed.data.cssClass ?? cssClass;
      id = parsed.data.id;
    }
    await this.addBotCard({ type: BotCardType.youtubeCard, data: { id: id as string, cssClass } });
  }

  /**
   * Zeigt eine Vimeo-Video-Card an.
   * @param id Die Vimeo-Video-ID, oder ein Objekt gemäß `cardsVimeoSchema`.
   * @param hash Privater Vimeo-Hash für geschickte Videos.
   * @param cssClass Optionale CSS-Klasse für die Card.
   * @returns Nichts.
   */
  public async vimeo(id: string | z.input<typeof cardsVimeoSchema>, hash?: string, cssClass: string = ''): Promise<void> {
    const parsed = cardsVimeoSchema.safeParse(id);
    if (parsed.success && typeof id === 'object') {
      hash = parsed.data.hash;
      cssClass = parsed.data.cssClass ?? cssClass;
      id = parsed.data.id;
    }
    await this.addBotCard({ type: BotCardType.vimeoCard, data: { id: id as string, hash, cssClass } });
  }

  /**
   * Zeigt eine Bild-Card an. Lädt die URL bei Bedarf aus einem Skill auf.
   * @param src Bild-URL, Skill-ID, oder ein Objekt gemäß `cardsImageSchema`.
   * @param altText Alternativtext für das Bild.
   * @param cssClass Optionale CSS-Klasse für die Card.
   * @returns Nichts.
   */
  public async image(src: string | z.input<typeof cardsImageSchema>, altText: string = '', cssClass: string = ''): Promise<void> {
    const parsed = cardsImageSchema.safeParse(src);
    if (parsed.success && typeof src === 'object') {
      altText = parsed.data.altText ?? altText;
      cssClass = parsed.data.cssClass ?? cssClass;
      src = parsed.data.src;
    }
    if (!(src as string).startsWith('http') && !(src as string).startsWith('data')) {
      src = await this.helperManager.getImageURlFromSkill(src as string);
    }
    await this.addBotCard({ type: BotCardType.imageCard, data: { src: src as string, altText, cssClass } });
  }

  /** Zeigt Navigations-Buttons für alle sichtbaren Stages an. */
  public async stageListButtons() {
    //const data: { text: string, onClick: () => void, className?: string }[] = [];
    const data: BUTTONCARDBUTTON[] = [];
    for (const stage of this.stageManager.visibleStages) {
      data.push({
        text: stage.title,
        onClick: "function (){ RAGAI.STAGE.goTo('" + stage.name + "') ; }",
        className: "stage-button"
      });
    }
    await this.buttons(data);
  }

  /**
   * Fügt eine beliebige Bot-Card zum Chatverlauf hinzu.
   * @param payload Card-Payload.
   * @param payload.type Der Card-Typ.
   * @param payload.data Die Card-spezifischen Daten.
   * @param triggerEvent `false`, um die zugehörigen `RAGAI.CARDS.ADD`-Events zu unterdrücken.
   * @returns Die gespeicherte Verlaufsnachricht.
   */
  public async addBotCard(payload: { type: string, data: unknown }, triggerEvent: boolean = true) {
    if (triggerEvent) {
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.CARDS.ADD", payload);
      this.eventManager._trigger_RAGAI_EVENT("RAGAI.CARDS.ADD." + payload.type, payload);
    }
    const backendPayload: DialogMessage = { content: [{ type: "text", text: JSON.stringify(payload) }], role: 'botcard', bypass: false };
    const res = await firstValueFrom(this._http.post<HistoryItemResponse[]>('/api/toolbox/membot/user/createmessagesinthread/' + this.botIdService.getBotId(), { messages: [backendPayload] }));
    if (res[0] != null) {
      return res[0];
    }
    throw new Error("Failed to add bot card");
  }
}
